/**
 * Sidebar.jsx — MUI Drawer-based navigation sidebar
 *
 * 9 nav items with flyout sub-tab navigation on hover.
 * Collapses to mini variant (icons only) with tooltips.
 * Active item highlighted with accent color.
 */

import { useState, useRef, useCallback, useMemo } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useIncompleteContactCount } from '../../hooks/useContacts';
import { useJobs } from '../../hooks/useJobs';
import { untriagedCount } from '../../lib/webLeads';
import { useRBAC } from '../../hooks/useRBAC';
import { useSettings } from '../../hooks/useSettings';
import { useTessieStatus } from '../../hooks/useTessie';
import { useFeedbackCount } from '../../hooks/useFeedback';
import { useTodoSummary } from '../../hooks/useTodos';
import UserAvatar from '../shared/UserAvatar';
import { getUserColor } from '../../lib/userColors';
import {
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Box,
  Typography,
  Tooltip,
  IconButton,
  Popper,
  Paper,
  Fade,
  Badge,
} from '@mui/material';
import ServerStats from './ServerStats';
import {
  Home as HomeIcon,
  SmartDisplay as RotoTubeIcon,
  ViewCarousel as CarouselIcon,
  Work as JobsIcon,
  People as PeopleIcon,
  CalendarMonth as CalendarIcon,
  Description as QuotesIcon,
  AttachMoney as InvoicesIcon,
  CreditCard as PayrollIcon,
  Shield as LegalIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  Menu as MenuIcon,
  Campaign as OutreachIcon,
  Mic as VoicenotesIcon,
  DirectionsCar as CarIcon,
  Link as LinksIcon,
  School as HowToIcon,
  MapsHomeWork as OpenHouseIcon,
  Checklist as TodoIcon,
} from '@mui/icons-material';
import { useFocus } from '../../hooks/useFocus';
import { navKey, navSubKey } from '../../lib/focusRegistry';
import FocusSwitch from './FocusSwitch';

const DRAWER_WIDTH_EXPANDED = 210;
const DRAWER_WIDTH_COLLAPSED = 56;

// Navigation items with optional flyout sub-tabs
export const NAV_ITEMS = [
  { path: '/',           label: 'Home',       icon: HomeIcon,       color: '#FFFFFF' },
  { path: '/todos',      label: 'To Do',      icon: TodoIcon,       color: '#38BDF8' },
  { path: '/rototube',   label: 'RotoTube',   icon: RotoTubeIcon,   color: '#EF4444' },
  { path: '/carousel',   label: 'Carousel',   icon: CarouselIcon,   color: '#4FBFCB' },
  { path: '/howto',      label: 'How To',     icon: HowToIcon,      color: '#10B981' },
  { path: '/jobs',       label: 'Jobs',       icon: JobsIcon,       color: '#13B5EA',
    flyout: [
      { label: 'Inquiries', path: '/jobs/inquiries' },
      { label: 'Quick Entry', param: 'action', value: 'newJob', isAction: true },
      { label: 'RE Quote', path: '/quotes', param: 'mode', value: 're', feature: 'quotes', level: 'create_edit' },
      { label: 'Custom Quote', path: '/quotes', param: 'mode', value: 'custom', feature: 'quotes', level: 'create_edit' },
      { label: 'VirTour Quote', path: '/quotes', param: 'mode', value: 'virtour', feature: 'quotes', level: 'create_edit' },
    ],
  },
  { path: '/people',     label: 'People',     icon: PeopleIcon,     color: '#2DD4BF',
    flyout: [
      { label: 'Contacts', param: 'tab', value: 'Contacts' },
      { label: 'Leaderboard', param: 'tab', value: 'Leaderboard' },
      { label: '+ New Contact', param: 'action', value: 'newContact', isAction: true },
    ],
  },
  { path: '/calendar',   label: 'Calendar',   icon: CalendarIcon,   color: '#A78BFA',
    flyout: [
      { label: '+ Add Event', param: 'action', value: 'addEvent', isAction: true },
    ],
  },
  { path: '/quotes',     label: 'Quotes',     icon: QuotesIcon,     color: '#FBBF24',
    flyout: [
      { label: 'Builder', param: 'tab', value: 'builder' },
      { label: 'Saved Quotes', param: 'tab', value: 'browser' },
      { label: 'Pay Split', param: 'tab', value: 'calculator' },
    ],
  },
  { path: '/invoices',   label: 'Invoices',   icon: InvoicesIcon,   color: '#4ADE80',
    flyout: [
      { label: '+ New Invoice', param: 'action', value: 'newInvoice', isAction: true },
    ],
  },
  { path: '/payroll',    label: 'Payroll',    icon: PayrollIcon,    color: '#A3E635',
    flyout: [
      { label: 'Crew Accounting', param: 'section', value: 'crew', feature: 'payroll', level: 'full' },
      { label: 'Company Accounting', param: 'section', value: 'company', feature: 'payroll', level: 'full' },
    ],
  },
  { path: '/outreach',   label: 'Outreach',   icon: OutreachIcon,   color: '#F472B6' },
  { path: '/openhouse',  label: 'Open Houses', icon: OpenHouseIcon, color: '#E879F9',
    flyout: [
      { label: 'Browse', param: 'tab', value: 'browse' },
      { label: 'Plan', param: 'tab', value: 'plan' },
      { label: 'Run', param: 'tab', value: 'run' },
      { label: 'Follow-up', param: 'tab', value: 'followup' },
      { label: 'Playbook', param: 'tab', value: 'playbook' },
    ],
  },
  { path: '/voicenotes', label: 'Voice Notes', icon: VoicenotesIcon, color: '#F97316' },
  { path: '/links',      label: 'Links',       icon: LinksIcon,      color: '#13B5EA' },
  { path: '/vehicle',    label: 'Vehicle',     icon: CarIcon,        color: '#60A5FA',
    flyout: [
      { label: 'Drive Log', param: 'tab', value: 'drives' },
      { label: 'Mileage Summary', param: 'tab', value: 'summary' },
    ],
  },
  { path: '/legal',      label: 'Legal',      icon: LegalIcon,      color: '#CBD5E1',
    flyout: [
      { label: 'Documents', param: 'tab', value: '0' },
      { label: 'ICA Generator', param: 'tab', value: '1' },
    ],
  },
  { path: '/settings',   label: 'Settings',   icon: SettingsIcon,   color: '#D1D5DB',
    flyout: [
      { label: 'Profile', param: 'tab', value: 'profile' },
      { label: 'Team', param: 'tab', value: 'team', feature: 'settings', level: 'company' },
      { label: 'Finance', param: 'tab', value: 'finance', feature: 'settings', level: 'company' },
      { label: 'Integrations', param: 'tab', value: 'integrations', feature: 'settings', level: 'full' },
      { label: 'Data', param: 'tab', value: 'data', feature: 'settings', level: 'full' },
      // Either full level can restore its own table's entries (routes/trash.js).
      { label: 'Trash', param: 'tab', value: 'trash', anyOf: [['jobs', 'full'], ['invoices', 'full']] },
      { label: 'Feedback', param: 'tab', value: 'feedback', tool: 'activity' },
      { label: 'Activity Log', param: 'tab', value: 'activity', tool: 'activity' },
    ],
  },
];

export default function Sidebar({ expanded, onToggle }) {
  const { logout, profile } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { data: allJobs = [] } = useJobs();
  const { hasAction, hasAccess, hasToolAccess } = useRBAC();
  const { data: settings } = useSettings();
  const { data: incompleteCount = 0 } = useIncompleteContactCount({ enabled: hasAccess('crm', 'view_no_financials', settings) });
  // New web leads awaiting triage (Inquiries inbox) — server already scopes
  // the jobs list per role, so non-admin users naturally count 0.
  const newLeadCount = useMemo(() => untriagedCount(allJobs), [allJobs]);
  const vehicleAccess = hasToolAccess('vehicle', settings);
  const { data: tessieStatus } = useTessieStatus({ enabled: !!vehicleAccess });
  const unreviewedDrives = tessieStatus?.unreviewedCount || 0;
  // Bug / idea box inbox — only the roles that can triage it fetch or see the count.
  const feedbackAccess = hasToolAccess('activity', settings);
  const { data: feedbackCount } = useFeedbackCount({ enabled: !!feedbackAccess });
  const newFeedback = feedbackAccess ? (feedbackCount?.new || 0) : 0;
  // My open to-dos: red when any is overdue, grey otherwise. External users have no list.
  const { data: todoSummary } = useTodoSummary({ enabled: !!profile && !profile.is_external });
  const todoOpen = todoSummary?.openCount || 0;
  const todoOverdue = (todoSummary?.overdueCount || 0) > 0;
  const drawerWidth = expanded ? DRAWER_WIDTH_EXPANDED : DRAWER_WIDTH_COLLAPSED;
  const { isHidden } = useFocus();

  // Filter nav items based on permissions (new level-based system)
  const visibleNavItems = useMemo(() => {
    const PERM_MAP = {
      '/jobs':        { feature: 'jobs',       level: 'view_assigned' },
      '/people':      { feature: 'crm',        level: 'view_no_financials' },
      '/calendar':    { feature: 'calendar',   level: 'own_schedule' },
      '/quotes':      { feature: 'quotes',     level: 'view_assigned' },
      '/invoices':    { feature: 'invoices',   level: 'view_assigned' },
      '/payroll':     { feature: 'payroll',    level: 'own_total' },
      '/legal':       { feature: 'legal',      level: 'own_contracts' },
      '/calculator':  { feature: 'calculator', level: 'own_earnings' },
      '/outreach':    { feature: 'outreach',   level: 'full' },
      '/settings':    { feature: 'settings',   level: 'profile_only' },
      '/voicenotes':  { tool: 'voicenotes' },
      '/links':       { tool: 'links' },
      '/vehicle':     { tool: 'vehicle' },
      '/openhouse':   { tool: 'openhouse' },
      '/howto':       { feature: 'wiki',       level: 'view' },
    };
    const permitted = NAV_ITEMS.filter(item => {
      // External contributors see only Staycation
      if (profile?.is_external) return item.path === '/projects';
      const perm = PERM_MAP[item.path];
      if (!perm) return true;
      if (perm.tool) return hasToolAccess(perm.tool, settings);
      return hasAccess(perm.feature, perm.level, settings);
    });
    // Focus: drop hidden tools and flyout targets — but never the screen you're on,
    // or reaching it by link or Ctrl+K would leave you with no highlighted nav item.
    return permitted
      .filter((item) => !isHidden(navKey(item.path)) || location.pathname === item.path
        || (item.path !== '/' && location.pathname.startsWith(item.path)))
      .map((item) => {
        if (!item.flyout) return item;
        // A chip's own feature/level (or tool) mirrors the gate on the tab or route it opens.
        const flyout = item.flyout.filter((t) => !isHidden(navSubKey(item.path, t))
          && (t.tool ? hasToolAccess(t.tool, settings)
            : t.anyOf ? t.anyOf.some(([f, l]) => hasAccess(f, l, settings))
            : !t.feature || hasAccess(t.feature, t.level, settings)));
        return { ...item, flyout: flyout.length ? flyout : undefined };
      });
  }, [hasAccess, hasToolAccess, settings, profile, isHidden, location.pathname]);

  // Flyout hover state
  const [flyoutItem, setFlyoutItem] = useState(null);
  const [flyoutAnchor, setFlyoutAnchor] = useState(null);
  const enterTimer = useRef(null);
  const leaveTimer = useRef(null);

  const handleNavEnter = useCallback((e, item) => {
    if (!item.flyout) return;
    clearTimeout(leaveTimer.current);
    const el = e.currentTarget; // capture before async
    enterTimer.current = setTimeout(() => {
      setFlyoutAnchor(el);
      setFlyoutItem(item);
    }, 120);
  }, []);

  const handleNavLeave = useCallback(() => {
    clearTimeout(enterTimer.current);
    leaveTimer.current = setTimeout(() => {
      setFlyoutItem(null);
      setFlyoutAnchor(null);
    }, 150);
  }, []);

  const handleFlyoutEnter = useCallback(() => {
    clearTimeout(leaveTimer.current);
  }, []);

  const handleFlyoutLeave = useCallback(() => {
    leaveTimer.current = setTimeout(() => {
      setFlyoutItem(null);
      setFlyoutAnchor(null);
    }, 150);
  }, []);

  const handleFlyoutClick = useCallback((item, target) => {
    setFlyoutItem(null);
    setFlyoutAnchor(null);
    const base = target.path || item.path;
    // Plain-path targets (e.g. Jobs → Inquiries) navigate without params.
    navigate(target.param ? `${base}?${target.param}=${target.value}` : base);
  }, [navigate]);

  return (
    <>
    <Drawer
      data-track-area="sidebar"
      variant="permanent"
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        transition: 'width 200ms',
        '& .MuiDrawer-paper': {
          width: drawerWidth,
          overflowX: 'hidden',
          transition: 'width 200ms',
        },
      }}
    >
      {/* Header — Logo + Collapse Toggle */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: expanded ? 'space-between' : 'center',
          px: expanded ? 1.5 : 0.5,
          height: 56,
          borderBottom: 1,
          borderColor: 'secondary.dark',
        }}
      >
        {expanded && (
          <Typography
            variant="h5"
            sx={{
              fontFamily: '"Bebas Neue", sans-serif',
              color: 'white',
              letterSpacing: '0.1em',
              ml: 0.5,
            }}
          >
            RFOS
          </Typography>
        )}
        <IconButton onClick={onToggle} sx={{ color: 'rgba(255,255,255,0.5)', '&:hover': { color: 'white' } }}>
          <MenuIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Navigation Items */}
      <List sx={{ flex: 1, overflowY: 'auto', py: 1, px: 0.5 }}>
        {visibleNavItems.map((item) => {
          const Icon = item.icon;
          const isActive =
            location.pathname === item.path ||
            (item.path !== '/' && location.pathname.startsWith(item.path));

          const button = (
            <ListItemButton
              key={item.path}
              component={NavLink}
              to={item.path}
              data-focus={navKey(item.path)}
              onMouseEnter={(e) => handleNavEnter(e, item)}
              onMouseLeave={handleNavLeave}
              sx={{
                borderRadius: 1,
                mb: 0.25,
                minHeight: 40,
                px: expanded ? 1.5 : 1,
                justifyContent: expanded ? 'initial' : 'center',
                bgcolor: isActive ? 'rgba(19, 181, 234, 0.2)' : 'transparent',
                color: isActive ? 'white' : 'rgba(255,255,255,0.6)',
                '&:hover': {
                  bgcolor: isActive ? 'rgba(19, 181, 234, 0.25)' : 'rgba(255,255,255,0.1)',
                  color: 'white',
                },
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: expanded ? 1.5 : 0,
                  justifyContent: 'center',
                  color: isActive ? 'white' : item.color,
                }}
              >
                {item.path === '/jobs' && newLeadCount > 0 ? (
                  <Badge badgeContent={newLeadCount} color="warning" max={99} sx={{ '& .MuiBadge-badge': { fontSize: '0.55rem', height: 14, minWidth: 14, right: -2, top: -2 } }}>
                    <Icon fontSize="small" />
                  </Badge>
                ) : item.path === '/people' && incompleteCount > 0 ? (
                  <Badge badgeContent={incompleteCount} color="warning" sx={{ '& .MuiBadge-badge': { fontSize: '0.55rem', height: 14, minWidth: 14, right: -2, top: -2 } }}>
                    <Icon fontSize="small" />
                  </Badge>
                ) : item.path === '/vehicle' && unreviewedDrives > 0 ? (
                  <Badge badgeContent={unreviewedDrives} color="warning" max={99} sx={{ '& .MuiBadge-badge': { fontSize: '0.55rem', height: 14, minWidth: 14, right: -2, top: -2 } }}>
                    <Icon fontSize="small" />
                  </Badge>
                ) : item.path === '/todos' && todoOpen > 0 ? (
                  <Badge
                    badgeContent={todoOpen}
                    color={todoOverdue ? 'error' : 'default'}
                    max={99}
                    sx={{ '& .MuiBadge-badge': { fontSize: '0.55rem', height: 14, minWidth: 14, right: -2, top: -2, ...(todoOverdue ? {} : { bgcolor: 'grey.600', color: 'common.white' }) } }}
                  >
                    <Icon fontSize="small" />
                  </Badge>
                ) : item.path === '/settings' && newFeedback > 0 ? (
                  <Badge badgeContent={newFeedback} color="warning" max={99} sx={{ '& .MuiBadge-badge': { fontSize: '0.55rem', height: 14, minWidth: 14, right: -2, top: -2 } }}>
                    <Icon fontSize="small" />
                  </Badge>
                ) : (
                  <Icon fontSize="small" />
                )}
              </ListItemIcon>
              {expanded && (
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{ fontSize: '0.875rem', noWrap: true }}
                />
              )}
            </ListItemButton>
          );

          // Wrap in Tooltip when collapsed (but not when flyout is showing for this item)
          return expanded ? (
            <Box key={item.path}>{button}</Box>
          ) : (
            <Tooltip key={item.path} title={flyoutItem?.path === item.path ? '' : item.label} placement="right" arrow>
              {button}
            </Tooltip>
          );
        })}
      </List>

      {/* Server Performance Widget */}
      {!isHidden('sidebar.serverStats') && (
        <Box data-focus="sidebar.serverStats">
          <Divider sx={{ borderColor: 'secondary.dark' }} />
          <ServerStats expanded={expanded} />
        </Box>
      )}

      {/* Footer — Focus switch + User + Logout */}
      <Divider sx={{ borderColor: 'secondary.dark' }} />
      <Box sx={{ px: expanded ? 1 : 0.5, py: 1 }}>
        <FocusSwitch compact={!expanded} />
        {/* User info */}
        {profile && (
          <Box sx={{ px: 1, py: 0.75, mb: 0.5, display: 'flex', alignItems: 'center', gap: expanded ? 1 : 0, justifyContent: expanded ? 'flex-start' : 'center' }}>
            <UserAvatar
              name={profile.full_name || profile.email}
              color={getUserColor(profile)}
              avatarUrl={profile.avatar_url}
              size={expanded ? 'md' : 'sm'}
            />
            {expanded && (
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" sx={{ color: 'white', lineHeight: 1.2 }} noWrap>
                  {profile.full_name || profile.email}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.65rem' }}
                >
                  {profile.role?.replace(/_/g, ' ')}
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {/* Logout button */}
        <Tooltip title="Sign out" placement="right" disableHoverListener={expanded}>
          <ListItemButton
            onClick={logout}
            sx={{
              borderRadius: 1,
              minHeight: 40,
              px: expanded ? 1.5 : 1,
              justifyContent: expanded ? 'initial' : 'center',
              color: 'rgba(255,255,255,0.5)',
              '&:hover': {
                color: '#f87171',
                bgcolor: 'rgba(248, 113, 113, 0.1)',
              },
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: 0,
                mr: expanded ? 1.5 : 0,
                justifyContent: 'center',
                color: 'inherit',
              }}
            >
              <LogoutIcon fontSize="small" />
            </ListItemIcon>
            {expanded && (
              <ListItemText
                primary="Sign Out"
                primaryTypographyProps={{ fontSize: '0.875rem' }}
              />
            )}
          </ListItemButton>
        </Tooltip>
      </Box>
    </Drawer>

    {/* Flyout sub-tab popover — rendered outside Drawer to avoid overflow clipping */}
    <Popper
      data-track-area="sidebar"
      open={Boolean(flyoutItem && flyoutAnchor)}
      anchorEl={flyoutAnchor}
      placement="right-start"
      transition
      disablePortal={false}
      sx={{ zIndex: 1300 }}
      modifiers={[{ name: 'offset', options: { offset: [0, 4] } }]}
    >
      {({ TransitionProps }) => (
        <Fade {...TransitionProps} timeout={150}>
          <Paper
            onMouseEnter={handleFlyoutEnter}
            onMouseLeave={handleFlyoutLeave}
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 0.5,
              p: 0.75,
              bgcolor: '#1B2A4A',
              border: '1px solid rgba(255,255,255,0.12)',
              borderRadius: 1.5,
              maxWidth: 340,
              boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
            }}
          >
            {flyoutItem?.flyout?.map((target) => (
              <Box
                key={target.value || target.path || target.label}
                data-track={navSubKey(flyoutItem.path, target)}
                data-focus={navSubKey(flyoutItem.path, target)}
                onClick={() => handleFlyoutClick(flyoutItem, target)}
                sx={{
                  px: 1.25,
                  py: 0.5,
                  fontSize: '0.7rem',
                  fontWeight: 500,
                  borderRadius: '12px',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  transition: 'all 120ms',
                  ...(target.isAction
                    ? {
                        color: 'rgba(255,255,255,0.7)',
                        border: '1px solid rgba(255,255,255,0.2)',
                        bgcolor: 'transparent',
                        '&:hover': { bgcolor: 'rgba(19, 181, 234, 0.15)', borderColor: 'rgba(19,181,234,0.4)', color: 'white' },
                      }
                    : {
                        color: 'rgba(255,255,255,0.85)',
                        bgcolor: 'rgba(255,255,255,0.08)',
                        border: '1px solid transparent',
                        '&:hover': { bgcolor: 'rgba(19, 181, 234, 0.25)', color: 'white' },
                      }),
                }}
              >
                {target.label}
                {target.path === '/jobs/inquiries' && newLeadCount > 0 ? ` (${newLeadCount})` : ''}
                {flyoutItem.path === '/settings' && target.value === 'feedback' && newFeedback > 0 ? ` (${newFeedback})` : ''}
              </Box>
            ))}
          </Paper>
        </Fade>
      )}
    </Popper>
    </>
  );
}

export { DRAWER_WIDTH_EXPANDED, DRAWER_WIDTH_COLLAPSED };
